import { useState } from "react";
import CMSTeaser from "~/features/cms/components/cmsTeaser/CMSTeaser";
import { type CMSTeaserProps } from "~/features/cms/types/model";
import { NARROW, WIDE } from "~/utils/constants";
import { CMSSection } from "~/features/cms/components/cmsSection/CMSSection";
import type { CMSSectionComponentProps } from "~/features/cms/types/props";
import type ComponentTypes from "~/features/cms/componentTypes";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { CMSTeaserContainer } from "~/features/cms/components/cmsTeaser/CMSTeaserContainer";
import { type CMSFilterableTileContainerFilterDTO } from "~/features/cms/components/cmsFilterableTile/dto";
import { useTileFilter } from "~/features/cms/components/cmsFilterableTile/useTileFilter";
import classNames from "classnames";
import { makeExternallyStyledBaseElement } from "~/features/cms/utils/styles";
import CloseIcon from "@mui/icons-material/Close";

export enum DesktopContainerTypes {
  DEFAULT = "DEFAULT",
  CAROUSEL = "CAROUSEL",
}

export enum MobileContainerTypes {
  DEFAULT = "DEFAULT",
  HORIZONTAL = "HORIZONTAL_SCROLL",
  CAROUSEL = "CAROUSEL",
}

export interface CMSTeasersProps {
  id: string;
  type: ComponentTypes.CMSTeaserContainer;
  teasers: CMSTeaserProps[];
  scrollType?: DesktopContainerTypes;
  itemsPerRow?: number;
  mobileScrollType?: MobileContainerTypes;
  mobileItemsPerRow?: number;
  className?: string;
  filters?: CMSFilterableTileContainerFilterDTO[];
  showAllLabel?: string;
}

export const CMSTeasers = ({
  scrollType = DesktopContainerTypes.DEFAULT,
  itemsPerRow = 1,
  mobileScrollType = MobileContainerTypes.DEFAULT,
  mobileItemsPerRow = 1,
  teasers,
  filters = [],
  className,
  showAllLabel,
}: CMSTeasersProps) => {
  const isDesktop = useIsMinDesktopSize();

  const toggle = (index: number) => setOpenIndex((prevValue) => (index === prevValue ? null : index));
  const closeContent = () => setOpenIndex(null);

  let wideness: typeof WIDE | typeof NARROW;
  if (isDesktop) {
    wideness = itemsPerRow > 2 ? NARROW : WIDE;
  } else {
    wideness = NARROW;
  }
  const renderItem = (item: CMSTeaserProps) => (
    <CMSTeaser
      key={item.index}
      {...item}
      setContent={(content) => setContent({ content, index: content ? item.index : -1 })}
      onToggle={toggle}
      isExpanded={isTeaserExpanded(item.index)}
      wideness={wideness}
    />
  );

  // TODO: Temporary code, need to assign index, wrong type is coming above
  const tiles: CMSTeaserProps[] = teasers.map((teaser, index) => ({ ...teaser, index }));

  const { filterContainer, visibleTiles } = useTileFilter({ filters, showAllLabel, allTiles: tiles });

  const [{ content, index: contentIndex }, setContent] = useState<{
    content: CMSSectionComponentProps | null;
    index: number;
  }>({
    content: null,
    index: -1,
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const isTeaserExpanded = (index: number) => index === openIndex;

  const renderTeaserDetailsContainer = (validIds: number[]) => {
    if (openIndex === null || !validIds.includes(openIndex)) {
      return null;
    }

    const className = content?.fullWidth === true ? "scha__teasers__target__full" : "scha__teasers__target";

    return (
      <OpenDetailsLayer
        className={"scha__teasers__container scha__teasers__container--active"}
        rootStyles={content?.rootStyles}
        key={contentIndex}
      >
        <div className={"scha__teasers__x"} onClick={closeContent} style={{ position: "relative" }}>
          <CloseIcon sx={{ fontSize: 24 }} />
        </div>
        {content && (
          <div className={className}>
            <CMSSection {...content} rootStyles={undefined} />
          </div>
        )}
      </OpenDetailsLayer>
    );
  };

  return (
    <div className={classNames("scha__cms__filterable-container", className)}>
      {filterContainer}

      <CMSTeaserContainer
        scrollType={scrollType}
        itemsPerRow={itemsPerRow}
        mobileScrollType={mobileScrollType}
        mobileItemsPerRow={mobileItemsPerRow}
        className={className}
        teasers={visibleTiles}
        renderTeaser={renderItem}
        renderTeaserDetailsContainer={renderTeaserDetailsContainer}
      />
    </div>
  );
};

const OpenDetailsLayer = makeExternallyStyledBaseElement("div");
